import { INocfoPermissions, useCurrentUser } from '@root/query'
import { createContext, useContext, useMemo } from 'react'
import { BusinessContext } from './BusinessContext'

const PermissionContext = createContext<{ permissions: INocfoPermissions | null }>({
  permissions: null
})

export const PermissionContextProviderV2: React.FC<{ children: React.ReactNode }> = ({
  children
}) => {
  const { slug } = useContext(BusinessContext)
  const { data } = useCurrentUser({
    placeholderData: prev => prev
  })

  const currentUsership = useMemo(
    () => data?.userships.find(usership => usership.business_id === slug),
    [slug, data]
  )

  return (
    <PermissionContext.Provider value={{ permissions: currentUsership?.permissions }}>
      {children}
    </PermissionContext.Provider>
  )
}

export type PermissionKey = keyof INocfoPermissions
export type PermissionList = ReadonlyArray<PermissionKey>

/** Same check as `usePermission`, for a usership that is not the open business. */
export const hasPermission = (
  permissions: INocfoPermissions | null | undefined,
  requiredPermission?: PermissionList
): boolean => {
  if (!permissions) return false
  if (permissions.admin.value) return true
  if (requiredPermission == null) return false
  return requiredPermission.some(permission => permissions[permission]?.value)
}

export const usePermission = (requiredPermission?: PermissionList): boolean => {
  const { permissions } = useContext(PermissionContext)
  return hasPermission(permissions, requiredPermission)
}

/** True once the current usership permissions object is available. */
export const usePermissionsLoaded = (): boolean => {
  const { permissions } = useContext(PermissionContext)
  return permissions != null
}

interface PermissionBoundaryProps {
  required?: PermissionList
  children: React.ReactNode
  /** Shown when permissions are known and the user lacks access. */
  alternativeContent?: React.ReactNode
  /** Shown while permissions are still loading (keeps layout stable). */
  placeholder?: React.ReactNode
}

export const PermissionBoundary: React.FC<PermissionBoundaryProps> = ({
  required,
  children,
  alternativeContent = null,
  placeholder = null
}) => {
  const permissionsLoaded = usePermissionsLoaded()
  const allowed = usePermission(required)

  if (!permissionsLoaded) {
    return <>{placeholder}</>
  }
  return allowed ? <>{children}</> : <>{alternativeContent}</>
}

export class PermissionSet {
  static readonly ADMIN: PermissionList = ['admin'] as const

  static readonly INVOICING_EDIT: PermissionList = ['invoicing_admin', 'invoicing_editor'] as const
  static readonly INVOICING_ADMIN: PermissionList = ['invoicing_admin'] as const
  static readonly INVOICING_VIEW: PermissionList = [
    'invoicing_read',
    'invoicing_editor',
    'invoicing_admin'
  ] as const

  static readonly CONTACT_EDIT: PermissionList = ['contact_editor'] as const
  static readonly CONTACT_VIEW: PermissionList = ['contact_read', 'contact_editor'] as const

  static readonly REPORTING_EDIT: PermissionList = ['reporting_admin', 'reporting_editor'] as const
  static readonly REPORTING_ADMIN: PermissionList = ['reporting_admin'] as const
  static readonly REPORTING_VIEW: PermissionList = [
    'reporting_read',
    'reporting_editor',
    'reporting_admin'
  ] as const

  static readonly BOOKKEEPING_ADMIN: PermissionList = ['bookkeeping_admin'] as const
  static readonly BOOKKEEPING_EDIT: PermissionList = [
    'bookkeeping_admin',
    'bookkeeping_editor'
  ] as const
  static readonly BOOKKEEPING_FILES: PermissionList = [
    'bookkeeping_admin',
    'bookkeeping_editor',
    'bookkeeping_attachment'
  ] as const
  static readonly BOOKKEEPING_LOCK: PermissionList = [
    'bookkeeping_admin',
    'bookkeeping_lock_documents'
  ] as const
  static readonly BOOKKEEPING_VIEW: PermissionList = [
    'bookkeeping_read',
    'bookkeeping_editor',
    'bookkeeping_admin'
  ] as const

  // Shared date-range presets (DashboardFiltersView): bookkeeping or reporting read.
  static readonly DATE_RANGE_VIEW: PermissionList = [
    ...PermissionSet.BOOKKEEPING_VIEW,
    ...PermissionSet.REPORTING_VIEW
  ] as const

  static readonly ACCOUNTS_EDIT: PermissionList = ['accounts_editor'] as const
  static readonly ACCOUNTS_VIEW: PermissionList = ['accounts_read', 'accounts_editor'] as const

  static readonly FILES_EDIT: PermissionList = ['files_editor'] as const
  static readonly FILES_VIEW: PermissionList = ['files_read', 'files_editor'] as const

  static readonly SALARY: PermissionList = ['salary'] as const

  static readonly INTEGRATION_ADMIN: PermissionList = ['integration_admin'] as const

  /** Anyone who can open at least one Automaatiot hub tab. */
  static readonly WORKFLOWS_HUB: PermissionList = [
    ...PermissionSet.ADMIN,
    ...PermissionSet.BOOKKEEPING_ADMIN,
    ...PermissionSet.INVOICING_ADMIN
  ] as const

  static readonly FILES_UPLOAD: PermissionList = [
    ...PermissionSet.BOOKKEEPING_FILES,
    ...PermissionSet.INVOICING_EDIT,
    'files_editor'
  ] as const
}
